import { Navigate, Route, Routes } from "react-router-dom";
import ScrollToTop from "../components/ScrollToTop";
import { getIsCypressRunning, LD_CLIENT_SIDE_ID } from "../constants/constants";
import Nav, { Debugger } from "../components/Nav/Nav";
import DashboardPages from "../pages/DashboardPages/DashboardPages";
import TrendsPage from "../pages/TrendsPage";
import RedirectFromOldTilesPage from "../pages/RedirectFromOldTilesPage";
import CustomerTilesPage, {
  StyledSpinner,
} from "../pages/CustomerTilesPage/CustomerTilesPage";
import WorkSchedulesPage from "pages/WorkScheduleTilesPage/WorkScheduleTilesPage";
import Error403Component from "../components/SimpleComponents/Errors/Error403Component";
import Error400Component from "../components/SimpleComponents/Errors/Error400Component";
import HelpAndFeedbackPage from "../pages/HelpAndFeedbackPage";
import SettingsPage from "../components/Settings/SettingsPage";
import { ReportsRouter } from "reports/routers/ReportsRouter";
import GridLayoutPage from "../pages/GridLayoutPage/GridLayoutPage";
import { AppStylesProvider } from "./AppStylesProvider";
import { UpdateIsAvailableIndicator } from "./UpdateIsAvailableIndicator";
import SyncUrlParamsToStoreHooks from "../components/SimpleComponents/SyncUrlParamsToStore";
import { getTokenInfo } from "../contexts/AuthManager/Auth";
import { useAuth0 } from "@auth0/auth0-react";
import ErrorManager from "../contexts/ErrorManager/ErrorManager";
import { useLocation } from "react-router";
import AutoRotatePagesHooks from "components/AutoRotatePages";
import ComponentNamesTooltip from "components/SimpleComponents/ComponentNamesTooltip";
import SyncDocumentTitleToUrlHooks from "./SyncDocumentTitleToUrl";
import { LDProvider, basicLogger } from "launchdarkly-react-client-sdk";
import { TrackPerformanceMetricsOnIntervalHooks } from "./TrackPerformanceMetricsOnIntervalHooks";
import { PrintableStyles } from "utils/printerUtils";
import OperatorPage from "pages/OperatorPage";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useIsAdmin } from "utils/customHooks/genericHooks";
import QuickCustomerSwitcher from "components/QuickCustomerSwitcher";
import CustomAskrUrlIndicator from "./CustomAskrUrlIndicator";
import DataGetter from "components/DataGetter/DataGetter";
import { AppHooks } from "./AppHooks";
import FlightPerformanceObserver from "./FlightPerformanceObserver";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { AuthenticationFailedIndicator } from "./AuthenticationFailedIndicator";
import Tier0Page from "components/MeritTier0Page/Tier0Page";
import { AuthenticationFailedPage } from "./AuthenticationFailedPage";
import SnapshotsPage from "pages/SnapshotsPage/SnapshotsPage";
import DataDumpsDownloaderPage from "pages/DataDumpsDownloaderPage/DataDumpsDownloaderPage";
import AnalyticsPowerBIPage from "pages/AnalyticsPowerBIPage/AnalyticsPowerBIPage";
import ThroughputPowerBIPage from "pages/ThroughputPowerBIPage/ThroughputPowerBIPage";
import LogbookPowerBIPage from "pages/LogbookPowerBIPage/LogbookPowerBIPage";
import TimelineOnlyPage from "pages/TimelineOnlyPage/TimelineOnlyPage";
import ErrorOverlay from "reports/components/ErrorOverlay";
import { ConfigurationPage } from "pages/ConfigurationPage/ConfigurationPage";
import GetKioskPage from "pages/GetKioskPage/GetKioskPage";
import ServiceStatusPage from "../pages/ServiceStatusPage/ServiceStatusPage";
import ArbitraryPowerBIPage from "pages/DashboardPages/ArbitraryPowerBIPage/ArbitraryPowerBIPage";

// tslint:disable-next-line: no-big-function
function App(): React.JSX.Element {
  const isLoading = useIsLoading();
  const isStorybook = useIsStorybook();
  const isAdminUser = useIsAdmin();
  return (
    <>
      <ErrorBoundary small={true} overlay={true} id={"App_DataGetter"}>
        <DataGetter />
      </ErrorBoundary>
      <ErrorBoundary small={true} overlay={true} id={"App_AppHooks"}>
        <AppHooks />
      </ErrorBoundary>
      {isAdminUser && (
        <ErrorBoundary
          small={true}
          overlay={true}
          id={"App_QuickCustomerSwitcher"}
        >
          <QuickCustomerSwitcher />
        </ErrorBoundary>
      )}
      {!isStorybook && <TrackPerformanceMetricsOnIntervalHooks />}
      <CustomAskrUrlIndicator />
      {isLoading ? (
        <StyledSpinner className="App-spinner" />
      ) : (
        <PrintableStyles>
          <AppStylesProvider>
            {/* components ending with "Hooks" don't render anything to the page */}
            <SyncDocumentTitleToUrlHooks />
            <SyncUrlParamsToStoreHooks />
            <AutoRotatePagesHooks />
            <ComponentNamesTooltip />
            <UpdateIsAvailableIndicator />
            <AuthenticationFailedIndicator />
            <RedirectFromBadCallbackPages />
            <Nav />
            <ErrorOverlay />
            <Debugger />
            <ErrorBoundary
              small={true}
              overlay={true}
              id={"App_FlightPerformanceObserver"}
            >
              <FlightPerformanceObserver />
            </ErrorBoundary>
            {/* remove trailing slashes - these prevent useGetPageFromPath from working */}
            <RedirectRemoveTrailingSlashes />
            {/* ErrorManager sits below the Nav,
        so we can still access the Nav when an error page is rendered */}
            <ErrorManager>
              <ScrollToTop>
                <Routes>
                  <Route path="/" element={<CustomerTilesPage />} />
                  {/* storybook iframe */}
                  <Route path="/iframe.html" element={<CustomerTilesPage />} />
                  {/* if we get redirected to /callback/ without any search data, redirect to home page */}
                  <Route
                    path="/callback/"
                    element={<RedirectIfSearchIsEmpty />}
                  />
                  <Route path="/index.html" element={<CustomerTilesPage />} />
                  <Route
                    // "midgard process page" - allow accessing single-process (hublet) page via midgardId for simpler url (e.g. easy to link to here from an alert message)
                    path={`/:ckey/process/:midgardId`}
                    element={<DashboardPages />}
                  />
                  <Route
                    path={"/:ckey/schedule"}
                    element={<WorkSchedulesPage />}
                  />
                  <Route
                    path={`/:ckey/dashboard/:group?/:detail?`}
                    element={<DashboardPages />}
                  />
                  <Route path="/:ckey/trends" element={<TrendsPage />} />
                  <Route
                    path="/:ckey/grid/:group/:detail"
                    element={<GridLayoutPage />}
                  />
                  <Route
                    path="/:ckey/operator/:group/:detail"
                    element={<OperatorPage />}
                  />
                  <Route path="/:ckey/reports/*" element={<ReportsRouter />} />
                  <Route
                    path="/tiles/:ckey/:group?/:detail?"
                    element={<RedirectFromOldTilesPage />}
                  />
                  <Route path="/:ckey/tier0" element={<Tier0Page />} />
                  <Route path="/:ckey/settings" element={<SettingsPage />} />
                  <Route path="/:ckey/help" element={<HelpAndFeedbackPage />} />
                  <Route
                    path="/:ckey/config/:configType"
                    element={<ConfigurationPage />}
                  />
                  <Route path="/:ckey/snapshots" element={<SnapshotsPage />} />
                  <Route
                    path="/:ckey/dataDumps"
                    element={<DataDumpsDownloaderPage />}
                  />
                  <Route
                    path="/:ckey/sight-analytics"
                    element={<AnalyticsPowerBIPage />}
                  />
                  <Route
                    path="/:ckey/sight-throughput"
                    element={<ThroughputPowerBIPage />}
                  />
                  <Route
                    path="/:ckey/sight-logbook"
                    element={<LogbookPowerBIPage />}
                  />
                  <Route
                    path="/:ckey/sight/:reportId"
                    element={<ArbitraryPowerBIPage />}
                  />
                  <Route
                    path={`/:ckey/timeline/:group?/:detail?`}
                    element={<TimelineOnlyPage />}
                  />
                  <Route path="/unauthorized" element={<Error403Component />} />
                  <Route path="/bad-url" element={<Error400Component />} />
                  <Route
                    path="/auth-failed/"
                    element={<AuthenticationFailedPage />}
                  />
                  <Route path="/get-kiosk" element={<GetKioskPage />} />
                  <Route
                    path="/:ckey/service-status"
                    element={<ServiceStatusPage />}
                  />
                  <Route element={<Navigate replace={true} to="/" />} />
                </Routes>
              </ScrollToTop>
            </ErrorManager>
          </AppStylesProvider>
        </PrintableStyles>
      )}
    </>
  );
}

export default function AppWithLDProvider() {
  return (
    <LDProvider
      clientSideID={LD_CLIENT_SIDE_ID}
      context={{ kind: "user", key: "uninitialized" }}
      options={{
        logger: basicLogger({ level: "warn" }),
        sendEvents: false, // Disable sending events to LaunchDarkly to prevent CORS issues
        disableSyncEventPost: true, // Make sure events are not synchronized
        allAttributesPrivate: true, // Make all user attributes private to reduce API calls
        diagnosticOptOut: true, // Opt out of diagnostic events
      }}
    >
      <App />
    </LDProvider>
  );
}

/**
 * to prevent the loading spinner appearing early while we wait for the auth0 response,
 * check whether there's still a valid token + expiry in local storage
 * (which gets removed on logout)
 *
 * @param {boolean} isTest
 * @returns
 */
function useIsLoading() {
  const { isAuthenticated, isLoading } = useAuth0();
  const isCypressRunning = getIsCypressRunning();
  const { isTokenValid } = getTokenInfo();
  const isLoggedIn = isAuthenticated || isTokenValid;
  return !isCypressRunning && !isLoggedIn && isLoading;
}

/**
 * Auth0 uses /callback/ to pass the auth credentials though the search,
 * but sometimes the search is empty. If it's empty, redirect to '/'.
 */
function RedirectIfSearchIsEmpty() {
  const { search } = useLocation();
  const isSearchEmpty = search === "";
  return isSearchEmpty ? <Navigate replace={true} to="/" /> : <StyledSpinner />;
}

/**
 * if page is /callback/* without any search,
 * or if we're on a real page like /callback/snapshots?workSchedule=1,
 * redirect to home page
 */
function RedirectFromBadCallbackPages() {
  const { search, pathname } = useLocation();
  const isSearchEmpty = search === "";
  if (
    pathname.startsWith("/callback/") &&
    (isSearchEmpty || search.includes("workSchedule="))
  ) {
    return <Navigate replace={true} to="/" />;
  }
  return null;
}

function RedirectRemoveTrailingSlashes() {
  const { pathname, search, hash } = useLocation();
  const fullPath = pathname + search + hash;
  const endsWithSlash = fullPath.endsWith("/");

  return endsWithSlash ? (
    <Navigate replace={true} to={fullPath.slice(0, -1)} />
  ) : null;
}
